import { TournamentState } from '@ps/poker-games-tile-mappers';
import { Translation } from '@ps/web-shared-components';
import type { ReactNode } from 'react';
import React from 'react';
import type { IntlShape } from 'react-intl';

import ChipIcon from '../../../../../../assets/icons/chip.svg';
import ClockIcon from '../../../../../../assets/icons/clock.svg';
import LevelsIcon from '../../../../../../assets/icons/levels.svg';
import PlayersIcon from '../../../../../../assets/icons/players.svg';
import PrizePoolIcon from '../../../../../../assets/icons/prize.svg';
import StackIcon from '../../../../../../assets/icons/stack.svg';
import type { Tournament } from '../../../../../../types/MTT';
import getTimeFormatProps from '../../../../../../utils/tournaments/getTimeFormatProps';
import type { InfoItemProps } from '../InfoItem/InfoItem';

import type { InfoItemsProps, TournamentTimes } from './InfoItemsContainer';
import styles from './InfoItemsContainer.scss';
import InfoItemsAnnounced from './components/InfoItemsAnnounced';
import InfoItemsCancelled from './components/InfoItemsCancelled';
import InfoItemsCompleted from './components/InfoItemsCompleted';
import InfoItemsDefault from './components/InfoItemsDefault';
import InfoItemsLateRegistration from './components/InfoItemsLateRegistration';
import InfoItemsRegistering from './components/InfoItemsRegistering';
import InfoItemsRunning from './components/InfoItemsRunning';
import InfoItemsSeating from './components/InfoItemsSeating';
import {
  TXTCLI_Average_Stack,
  TXTCLI_Blinds,
  TXTCLI_Current_Level,
  TXTCLI_First_Prize,
  TXTCLI_Late_regX_ends_in,
  TXTCLI_Places_Paid,
  TXTCLI_Players,
  TXTCLI_Prize_Pool,
  TXTCLI_RegX_opens_in,
  TXTCLI_Running_for,
  TXTCLI_StartedX,
  TXTCLI_Starting_Chips,
  TXTCLI_Starts_in,
  TXTCLI_Total_players_2,
  TXTMOB_BB,
  TXTWCL_CompletedX
} from './utils.messages.json';

export const INFO_ITEMS_BY_STATE = {
  [TournamentState.ANNOUNCED]: (props: InfoItemsProps) => <InfoItemsAnnounced {...props} />,
  [TournamentState.REGISTERING]: (props: InfoItemsProps) => <InfoItemsRegistering {...props} />,
  [TournamentState.SEATING]: (props: InfoItemsProps) => <InfoItemsSeating {...props} />,
  [TournamentState.LATE_REGISTRATION]: (props: InfoItemsProps) => (
    <InfoItemsLateRegistration {...props} />
  ),
  [TournamentState.RUNNING]: (props: InfoItemsProps) => <InfoItemsRunning {...props} />,
  [TournamentState.CANCELLED]: (props: InfoItemsProps) => <InfoItemsCancelled {...props} />,
  [TournamentState.COMPLETED]: (props: InfoItemsProps) => <InfoItemsCompleted {...props} />,
  DEFAULT: (props: InfoItemsProps) => <InfoItemsDefault {...props} />
};

export const getStartingChipsItem = (tournament: Tournament, intl: IntlShape): InfoItemProps => {
  const { initStack, currentLevel } = tournament;
  const bigBlind = currentLevel?.stakes.blinds[1];

  const formattedInitStack = `${intl.formatNumber(initStack)}`;
  let value: ReactNode = formattedInitStack;
  if (bigBlind) {
    const bigBlindsForStack = intl.formatNumber(initStack / bigBlind, {
      maximumFractionDigits: 1
    });
    value = (
      <span>
        {formattedInitStack} ({bigBlindsForStack} <Translation {...TXTMOB_BB} />)
      </span>
    );
  }

  return {
    icon: <ChipIcon />,
    infoDetails: [
      {
        label: TXTCLI_Starting_Chips,
        value
      }
    ]
  };
};

export const getAverageStackItem = (tournament: Tournament, intl: IntlShape): InfoItemProps => {
  const { avgStack } = tournament;

  return {
    icon: <StackIcon />,
    infoDetails: [{ label: TXTCLI_Average_Stack, value: intl.formatNumber(avgStack) }]
  };
};

export const getTimeItemStartsIn = (timesData: TournamentTimes, intl: IntlShape): InfoItemProps => {
  const { startTimeMs, nowTimeMs } = timesData;
  const { translation, values } = getTimeFormatProps(startTimeMs, nowTimeMs);

  return {
    icon: <ClockIcon />,
    infoDetails: [
      {
        label: TXTCLI_Starts_in,
        value: intl.formatMessage(translation, values)
      }
    ]
  };
};

export const getTimeItemStartsInRegOpens = (
  timesData: TournamentTimes,
  intl: IntlShape
): InfoItemProps => {
  const { regStartTimeMs, nowTimeMs } = timesData;
  const { translation, values } = getTimeFormatProps(regStartTimeMs as number, nowTimeMs);
  const playersItem = getTimeItemStartsIn(timesData, intl);

  if (regStartTimeMs)
    playersItem.infoDetails.push({
      label: TXTCLI_RegX_opens_in,
      value: intl.formatMessage(translation, values)
    });

  return playersItem;
};

export const getTimeItemRunningFor = (
  timesData: TournamentTimes,
  intl: IntlShape
): InfoItemProps => {
  const { startTimeMs, nowTimeMs } = timesData;
  const { translation: runningTranslation, values: runningValues } = getTimeFormatProps(
    nowTimeMs,
    startTimeMs
  );

  return {
    icon: <ClockIcon />,
    infoDetails: [
      {
        label: TXTCLI_Running_for,
        value: intl.formatMessage(runningTranslation, runningValues)
      }
    ]
  };
};

export const getTimeItemCompleted = (
  timesData: TournamentTimes,
  timeZone: string,
  intl: IntlShape
): InfoItemProps => {
  const { startTimeMs, closedTimeMs } = timesData;

  return {
    icon: <ClockIcon />,
    infoDetails: [
      {
        label: TXTCLI_StartedX,
        value:
          intl.formatTime(startTimeMs, {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
            timeZone,
            timeZoneName: 'short'
          }) +
          ' ' +
          intl.formatDate(startTimeMs, {
            month: 'short',
            day: 'numeric'
          })
      },
      {
        label: TXTWCL_CompletedX,
        value:
          intl.formatTime(closedTimeMs, {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
            timeZone,
            timeZoneName: 'short'
          }) +
          ' ' +
          intl.formatDate(closedTimeMs, {
            month: 'short',
            day: 'numeric'
          })
      }
    ]
  };
};

export const getTimeItemLateRegistration = (
  timesData: TournamentTimes,
  intl: IntlShape
): InfoItemProps => {
  const { startTimeMs, nowTimeMs, lateRegTs } = timesData;
  const { translation: lateRegTranslation, values: lateRegValues } = getTimeFormatProps(
    lateRegTs * 1000,
    nowTimeMs
  );
  const { translation: runningTranslation, values: runningValues } = getTimeFormatProps(
    nowTimeMs,
    startTimeMs
  );

  return {
    icon: <ClockIcon />,
    infoDetails: [
      {
        label: TXTCLI_Running_for,
        value: intl.formatMessage(runningTranslation, runningValues)
      },
      {
        label: TXTCLI_Late_regX_ends_in,
        value: intl.formatMessage(lateRegTranslation, lateRegValues),
        className: styles.valueRed
      }
    ]
  };
};

export const getPrizePoolItem = (tournament: Tournament): InfoItemProps => ({
  icon: <PrizePoolIcon />,
  infoDetails: [{ label: TXTCLI_Prize_Pool, value: tournament.formattedPrizePool }]
});

export const getPrizePoolFirstPrizeItem = (tournament: Tournament): InfoItemProps => {
  const { formattedWinnerAward } = tournament;

  const prizePoolItem = getPrizePoolItem(tournament);
  prizePoolItem.infoDetails.push({ label: TXTCLI_First_Prize, value: formattedWinnerAward });

  return prizePoolItem;
};

export const getPlayersItem = (tournament: Tournament, intl: IntlShape): InfoItemProps => ({
  icon: <PlayersIcon />,
  infoDetails: [
    {
      label: TXTCLI_Players,
      value: intl.formatNumber(tournament.entrants)
    }
  ]
});

export const getPlayersPlacesPaidItem = (
  tournament: Tournament,
  intl: IntlShape
): InfoItemProps => {
  const playersItem = getPlayersItem(tournament, intl);
  playersItem.infoDetails.push({
    label: TXTCLI_Places_Paid,
    value: intl.formatNumber(tournament.paidPlaces)
  });

  return playersItem;
};

export const getTotalPlayersItem = (tournament: Tournament, intl: IntlShape): InfoItemProps => ({
  icon: <PlayersIcon />,
  infoDetails: [
    {
      label: TXTCLI_Total_players_2,
      value: intl.formatNumber(tournament.entrants)
    }
  ]
});

export const getBlindsItem = (tournament: Tournament): InfoItemProps => {
  const { currentLevel } = tournament;
  const [ante] = currentLevel.stakes.antes;
  const [smallBlind, bigBlind] = currentLevel.stakes.blinds;

  return {
    icon: <LevelsIcon />,
    infoDetails: [
      {
        label: TXTCLI_Current_Level,
        value: `${currentLevel.blindsLevel}`
      },
      {
        label: TXTCLI_Blinds,
        value: `${smallBlind}/${bigBlind} (${ante})`
      }
    ]
  };
};
